// Sheet assembly uses the SheetJS already in the bundle; only the WRITE needs
// the styled fork (the community build silently drops cell styles), and that
// loads on demand in downloadStatementXlsx — a megabyte no one pays for until
// they export. Both are the same 0.18.5 codebase, so the workbook object
// built here writes cleanly through either.
import * as XLSX from 'xlsx';

/**
 * Financial-statement Excel writer.
 *
 * The report pages export their matrices as-is (see downloadExcelFromCsv) —
 * a grid of numbers with a header row. A P&L is read differently: sections
 * are named, detail sits under its heading, totals rule off below the detail
 * they total, and the bottom line is unmistakable. This writes that document.
 *
 * Input rows come from buildFinancialPnlStatementRows / buildUsaliStatementRows
 * as `{ label, cells, indent, style }`, where style selects the typography:
 *
 *   title        property (or portfolio) heading
 *   section      REVENUE / EXPENSES / STATISTICS — ruled underneath
 *   group        a department or band heading
 *   detail       an account line
 *   groupTotal   "Total <department>" — single rule above
 *   sectionTotal "Total Revenue" — single rule above, shaded
 *   net          "NET INCOME" — single rule above, double rule below
 *   blank        spacer
 *
 * Amounts stay numeric (no pre-formatted strings), so the workbook stays
 * usable for arithmetic; presentation is carried entirely by number formats.
 */

const FONT = 'Calibri';
const INK = '111827';
const BODY = '1F2937';
const MUTED = '6B7280';
const RULE = '9CA3AF';
const SECTION_RULE = 'D1D5DB';
const TOTAL_FILL = 'F3F4F6';
const NET_FILL = 'E5E7EB';

// Accounting presentation: negatives in parentheses, thousands separated.
// Percent-unit values (69.3 → "69.3%") carry a literal % — the payloads are
// already in percent units, so Excel's own percent format would multiply by
// 100 a second time.
const NUM_FMT = {
  text: '@',
  number: '#,##0;(#,##0)',
  number1: '#,##0.0;(#,##0.0)',
  number2: '#,##0.00;(#,##0.00)',
  numberCompact: '#,##0;(#,##0)',
  hours: '#,##0.00;(#,##0.00)',
  currency: '#,##0;(#,##0)',
  currency2: '#,##0.00;(#,##0.00)',
  percent: '#,##0"%";(#,##0"%")',
  percent1: '#,##0.0"%";(#,##0.0"%")',
  percent2: '#,##0.00"%";(#,##0.00"%")',
};

const numFmtFor = (format) => NUM_FMT[format] || NUM_FMT.number;

const thin = { style: 'thin', color: { rgb: RULE } };
const double = { style: 'double', color: { rgb: RULE } };

/**
 * Per-style font / fill / border, applied to the label cell and every value
 * cell on the row so the rules read straight across the statement.
 */
const ROW_STYLES = {
  title: { font: { name: FONT, sz: 12, bold: true, color: { rgb: INK } } },
  section: {
    font: { name: FONT, sz: 11, bold: true, color: { rgb: INK } },
    border: { bottom: { style: 'thin', color: { rgb: SECTION_RULE } } },
  },
  group: { font: { name: FONT, sz: 10, bold: true, color: { rgb: BODY } } },
  detail: { font: { name: FONT, sz: 10, color: { rgb: BODY } } },
  groupTotal: {
    font: { name: FONT, sz: 10, bold: true, color: { rgb: BODY } },
    border: { top: thin },
  },
  sectionTotal: {
    font: { name: FONT, sz: 10.5, bold: true, color: { rgb: INK } },
    fill: { patternType: 'solid', fgColor: { rgb: TOTAL_FILL } },
    border: { top: thin },
  },
  net: {
    font: { name: FONT, sz: 11, bold: true, color: { rgb: INK } },
    fill: { patternType: 'solid', fgColor: { rgb: NET_FILL } },
    border: { top: thin, bottom: double },
  },
  blank: {},
};

const isBlankValue = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && !Number.isFinite(v));

/** A cell object for a value, typed numeric where the value is a number. */
function valueCell(value, format, style) {
  const numeric = typeof value === 'number' ? value : null;
  if (numeric !== null) {
    return { v: numeric, t: 'n', z: numFmtFor(format), s: { ...style, alignment: { horizontal: 'right' } } };
  }
  return { v: String(value), t: 's', s: { ...style, alignment: { horizontal: 'right' } } };
}

/**
 * Build one statement worksheet.
 *
 * @param {Object} spec
 * @param {string} [spec.title]             organization name
 * @param {string} [spec.subtitle]          statement name, e.g. "Profit & Loss · Departmental"
 * @param {string[]} [spec.metaLines]       period / filters / currency notes
 * @param {string} [spec.rowHeaderLabel]    header over the description column
 * @param {Array} spec.columns              visible matrix columns ({ key, label, group, format })
 * @param {Array} spec.rows                 statement rows ({ label, cells, indent, style, format?, hidden? })
 *                                          — `hidden` collapses the row behind its outline group
 * @param {number} [spec.labelWidth]        description column width
 * @returns {Object} a SheetJS worksheet
 */
export function buildStatementSheet({
  title = '',
  subtitle = '',
  metaLines = [],
  rowHeaderLabel = '',
  columns = [],
  rows = [],
  labelWidth = 46,
}) {
  const ws = {};
  const merges = [];
  const rowMeta = [];
  const lastCol = columns.length; // column 0 is the description
  let r = 0;

  const put = (row, col, cell) => {
    ws[XLSX.utils.encode_cell({ r: row, c: col })] = cell;
  };
  // Heading lines span the sheet so long titles are not clipped by the
  // description column's width.
  const heading = (text, style) => {
    if (text) {
      put(r, 0, { v: String(text), t: 's', s: style });
      if (lastCol > 0) merges.push({ s: { r, c: 0 }, e: { r, c: lastCol } });
    }
    r += 1;
  };

  heading(title, { font: { name: FONT, sz: 14, bold: true, color: { rgb: INK } } });
  heading(subtitle, { font: { name: FONT, sz: 11, bold: true, color: { rgb: BODY } } });
  metaLines.filter(Boolean).forEach((line) => heading(line, { font: { name: FONT, sz: 9, color: { rgb: MUTED } } }));
  r += 1; // spacer above the column headers

  // Column headers: month/Total group names on the first line (merged across
  // their metrics), the metric names beneath.
  const hasGroups = columns.some((c) => c.group);
  const headStyle = {
    font: { name: FONT, sz: 9, bold: true, color: { rgb: INK } },
    alignment: { horizontal: 'center' },
    border: { bottom: thin },
  };
  if (hasGroups) {
    put(r, 0, { v: '', t: 's', s: headStyle });
    let c = 1;
    while (c <= columns.length) {
      const group = columns[c - 1].group || '';
      let span = 1;
      while (c + span <= columns.length && (columns[c + span - 1].group || '') === group) span += 1;
      put(r, c, { v: group, t: 's', s: headStyle });
      for (let i = 1; i < span; i += 1) put(r, c + i, { v: '', t: 's', s: headStyle });
      if (span > 1) merges.push({ s: { r, c }, e: { r, c: c + span - 1 } });
      c += span;
    }
    r += 1;
  }
  const labelHeadStyle = {
    font: { name: FONT, sz: 9, bold: true, color: { rgb: INK } },
    alignment: { horizontal: 'right' },
    border: { bottom: { style: 'medium', color: { rgb: RULE } } },
  };
  put(r, 0, { v: rowHeaderLabel, t: 's', s: { ...labelHeadStyle, alignment: { horizontal: 'left' } } });
  columns.forEach((col, i) => {
    put(r, i + 1, {
      v: col.label || '',
      t: 's',
      s: col.align === 'left' ? { ...labelHeadStyle, alignment: { horizontal: 'left' } } : labelHeadStyle,
    });
  });
  r += 1;

  rows.forEach((row) => {
    const style = ROW_STYLES[row.style] || ROW_STYLES.detail;
    const indent = Math.max(0, Math.min(7, row.indent || 0));
    // In-workbook navigation from a contents row, e.g. "#'Rooms'!A1".
    // A cell hyperlink RECORD, optimized for Excel: label + click-through
    // there. Converters degrade it (Google Sheets shows the raw "#gid=…"
    // target as the text; Numbers shows the label without navigation) — a
    // HYPERLINK formula was tried instead and is WORSE: Numbers errors on it
    // and Google still mangles it. No xlsx link form survives all three.
    put(r, 0, {
      v: row.label || '',
      t: 's',
      s: { ...style, alignment: { horizontal: 'left', indent } },
      ...(row.link ? { l: { Target: row.link } } : {}),
    });
    columns.forEach((col, i) => {
      const value = row.cells ? row.cells[col.key] : undefined;
      // Rules run the full width even where a heading carries no amounts, so
      // a total's underline is not a dashed line of fragments.
      if (isBlankValue(value)) {
        if (style.border || style.fill) put(r, i + 1, { v: '', t: 's', s: style });
        return;
      }
      put(r, i + 1, valueCell(value, row.format || col.format, style));
    });
    // Outline levels let a reader collapse account detail back to the
    // department and section lines in Excel; rows marked hidden start
    // collapsed and expand from the outline controls. `outlineLevel` lets a
    // row join a deeper group than its label indent (e.g. a department
    // heading that hides with its accounts, leaving the total as summary).
    const level = Math.max(0, Math.min(7, row.outlineLevel ?? indent));
    rowMeta[r] = { level, ...(row.hidden ? { hidden: true } : {}) };
    r += 1;
  });

  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(r - 1, 0), c: lastCol } });
  ws['!merges'] = merges;
  ws['!rows'] = rowMeta;
  // Column outlines mirror the row treatment: columns carrying `level` join a
  // collapsible group (marked hidden to start collapsed), so a month can fold
  // its comparison columns behind the plan column via Excel's +/- controls.
  ws['!cols'] = [
    { wch: labelWidth },
    ...columns.map((c) => ({
      wch: c.format === 'text' ? 14 : Math.max(12, String(c.label || '').length + 6),
      ...(c.level ? { level: Math.max(0, Math.min(7, c.level)) } : {}),
      ...(c.hidden ? { hidden: true } : {}),
    })),
  ];
  return ws;
}

/** Worksheet tab names: ≤31 chars, no []:*?/\. */
export const sanitizeSheetName = (name) => String(name || 'Statement').replace(/[\\/?*[\]:]/g, ' ').substring(0, 31);

/** buildStatementSheet wrapped in a single-sheet workbook (the report pages' path). */
export function buildStatementWorkbook({ sheetName = 'Statement', ...spec }) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildStatementSheet(spec), sanitizeSheetName(sheetName));
  return wb;
}

/** Write any SheetJS workbook through the styled fork (loaded on demand). */
export async function downloadWorkbookXlsx(wb, filename) {
  const styled = await import('xlsx-js-style');
  const base = String(filename || 'statement').replace(/\.(csv|xlsx)$/i, '');
  styled.writeFile(wb, `${base}.xlsx`);
}

/** buildStatementWorkbook + browser download. `filename` may omit the extension. */
export async function downloadStatementXlsx({ filename, ...spec }) {
  await downloadWorkbookXlsx(buildStatementWorkbook(spec), filename);
}
